import React, { useState, useContext, useEffect } from "react";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import api from "services/api";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import toast from "react-hot-toast";
import { accountTypeOptions, accountStatusOptions } from "utils/data";

const CreateInviteCodeModal = ({ visible, handleClose }) => {
  const [code, setCode] = useState("");
  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm();
  useEffect(() => {
    reset({ duration: 30, count: 50 });
  }, []);

  const handleSave = async (data) => {
    if (!data["account_type"]) {
      return toast.error("Please select user account type");
    }
    const countNum = Number(data["count"]);
    if (isNaN(countNum)) {
      return toast.error("Invalid capacity number!");
    }

    const duartionDays = Number(data["duration"]);
    if (isNaN(duartionDays)) {
      return toast.error("Invalid duration days!");
    }

    try {
      const values = {
        code: data["code"].toUpperCase(),
        account_type: data["account_type"].value,
        count: countNum,
        valid_days: duartionDays,
      };

      await api.invitecode.create(values);

      handleClose();
      toast.success("Create new invite code successfully!");
    } catch (err) {
      const message = err?.response?.data?.message || "Could not create invite code";
      toast.error(message);
    }
  };

  return (
    <Dialog
      open={visible}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth={true}
    >
      <DialogTitle id="alert-dialog-title">New Invite Code</DialogTitle>

      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <form onSubmit={handleSubmit(handleSave)}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-start"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      required
                      textTransform="capitalize"
                    >
                      Account Type
                    </SuiTypography>
                  </SuiBox>
                  <Controller
                    rules={{ required: `Account is required.` }}
                    name="account_type"
                    control={control}
                    render={({ field }) => <SuiSelect {...field} options={accountTypeOptions} />}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="account_type"
                    render={({ message }) => (
                      <SuiTypography
                        variant="text"
                        fontWeight="regular"
                        fontSize="12px"
                        color="error"
                        className="ml-2"
                      >
                        {message}
                      </SuiTypography>
                    )}
                  />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-start"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      required
                      textTransform="capitalize"
                    >
                      Duration
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput
                    placeholder="30"
                    {...register("duration", {
                      required: `Duration is required.`,
                      min: {
                        value: 1,
                        message: `Minimal Duration should be greater than 1.`,
                      },
                    })}
                    icon={{
                      direction: "right",
                      component: <SuiTypography sx={{ mr: 1 }}>Days</SuiTypography>,
                    }}
                    type="number"
                  />

                  <ErrorMessage
                    errors={errors}
                    name="duration"
                    render={({ message }) => (
                      <SuiTypography
                        variant="text"
                        fontWeight="regular"
                        fontSize="12px"
                        color="error"
                        className="ml-2"
                      >
                        {message}
                      </SuiTypography>
                    )}
                  />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-start"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      required
                      textTransform="capitalize"
                    >
                      Invite Code
                    </SuiTypography>
                  </SuiBox>
                  <Controller
                    rules={{
                      required: `Code is required.`,
                      minLength: {
                        value: 6,
                        message: "Please input at least 6 characters!",
                      },
                    }}
                    name="code"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <SuiInput
                        value={code}
                        onChange={(e) => {
                          if (e?.target?.value) {
                            setCode(e.target.value.toUpperCase());
                          }

                          onChange(e);
                        }}
                        placeholder="UPSTYLED22"
                      />
                    )}
                  />

                  <ErrorMessage
                    errors={errors}
                    name="code"
                    render={({ message }) => (
                      <SuiTypography
                        variant="text"
                        fontWeight="regular"
                        fontSize="12px"
                        color="error"
                        className="ml-2"
                      >
                        {message}
                      </SuiTypography>
                    )}
                  />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-start"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                      required={true}
                    >
                      capacity
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput
                    placeholder="50"
                    {...register("count", {
                      required: `Capacity is required.`,
                      min: {
                        value: 1,
                        message: `Minimal Capacity should be greater than 1.`,
                      },
                    })}
                    type="number"
                  />
                  <ErrorMessage
                    errors={errors}
                    name="count"
                    render={({ message }) => (
                      <SuiTypography
                        variant="text"
                        fontWeight="regular"
                        fontSize="12px"
                        color="error"
                        className="ml-2"
                      >
                        {message}
                      </SuiTypography>
                    )}
                  />
                </SuiBox>
              </Grid>
            </Grid>
            <SuiBox
              pt={3}
              px={3}
              display="flex"
              justifyContent="end"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                  <SuiButton
                    variant="gradient"
                    size="medium"
                    onClick={handleClose}
                    className="mr-2"
                    color="light"
                  >
                    Cancel
                  </SuiButton>
                </SuiBox>
              </SuiBox>
              <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                  <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                    Create
                  </SuiButton>
                </SuiBox>
              </SuiBox>
            </SuiBox>
          </form>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default CreateInviteCodeModal;
